/**
 * The fit / fill button in the top-right corner of a remote picture on the
 * web call stage (pictureFit.ts), as on native (VRSLayout.native.tsx): a
 * 28 px circle 8 px in from the corner, in a 44 px target. It shows and hides
 * with the call bar, and it shows while it has keyboard focus.
 */
import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconEnterFullscreen, IconExitFullscreen } from '../../../base/icons/svg';
import { SELF_VIEW_CONTROL_SIZE } from './SelfViewDeviceSettings.web';
const FIT_BUTTON_SIZE = 28;
const useStyles = makeStyles()(theme => ({
    button: {
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        display: 'flex',
        height: SELF_VIEW_CONTROL_SIZE,
        justifyContent: 'center',
        padding: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        transition: 'opacity 220ms ease',
        width: SELF_VIEW_CONTROL_SIZE,
        zIndex: 5,
        '&:focus-visible': {
            opacity: 1,
            outline: 0,
            pointerEvents: 'auto'
        },
        '&:focus-visible > span': {
            outline: `2px solid ${theme.palette.focus01}`,
            outlineOffset: 2
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    },
    circle: {
        alignItems: 'center',
        background: theme.palette.ui01,
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        display: 'flex',
        height: FIT_BUTTON_SIZE,
        justifyContent: 'center',
        width: FIT_BUTTON_SIZE
    }
}));
const PictureFitButton = ({ fitByDefault, fitted, label, onToggle, participantId, visible }) => {
    const { classes, cx, theme } = useStyles();
    const onClick = useCallback(() => onToggle(participantId, fitByDefault), [fitByDefault, onToggle, participantId]);
    return (React.createElement("button", { "aria-label": label, className: cx(classes.button, !visible && classes.hidden), "data-vrs-fit": fitted ? 'fit' : 'fill', onClick: onClick, title: label, type: 'button' },
        React.createElement("span", { className: classes.circle },
            React.createElement(Icon, { color: theme.palette.icon01, size: 16, src: fitted ? IconEnterFullscreen : IconExitFullscreen }))));
};
export default PictureFitButton;
